import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from "@angular/common/http";
import { Observable, from, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";

import { AuthenticationService } from "../services/auth.service";
import { RETRIED_AFTER_REFRESH } from "./auth-http.context";

/** Convert raw DB/API error messages to user-friendly text for display in toasts */
function sanitizeErrorMessage(error: any): any {
  if (!error) return error;
  const toStr = (val: any) =>
    typeof val === "string" ? val : Array.isArray(val) ? val.join(", ") : String(val ?? "");
  const raw =
    typeof error === "object" && error?.message
      ? error.message
      : Array.isArray(error?.messages)
      ? error.messages
      : typeof error === "object" && error?.error != null
      ? error.error
      : error;
  const msg = toStr(typeof raw === "object" && raw?.message ? raw.message : raw);
  if (msg && /invalid\s+input\s+syntax\s+for\s+type\s+uuid/i.test(msg)) {
    return "Missing or invalid ID. Please refresh the page and try again.";
  }
  return error;
}

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private authenticationService: AuthenticationService) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err) => {
        if (err.status === 401) {
          if (this.authenticationService.isLoginRedirectInProgress()) {
            return throwError(() => err);
          }
          if (this.authenticationService.isIdentityRefreshRequest(request.url)) {
            this.authenticationService.clearSessionAndRedirectToLogin();
            return throwError(() => err);
          }
          if (request.context.get(RETRIED_AFTER_REFRESH)) {
            return throwError(() => err);
          }
          return this.refreshTokenAndRetry(request, next);
        }
        let error = err?.error?.message || err?.error?.messages || err;
        if (err?.error?.maxLimitForProduct) {
          error = {
            maxLimitForProduct: err?.error?.maxLimitForProduct,
            messages: error,
          };
        }
        error = sanitizeErrorMessage(error);
        return throwError(error);
      })
    );
  }

  /**
   * Uses AuthenticationService.refreshSession() as the single-flight refresh coordinator.
   * Concurrent 401s (and proactive refresh via JwtInterceptor) share the same in-flight refresh.
   */
  private refreshTokenAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.authenticationService.isLoginRedirectInProgress()) {
      return throwError(() => new Error("Login redirect in progress"));
    }

    const refreshToken = this.authenticationService.refreshToken;

    if (!refreshToken) {
      this.authenticationService.clearSessionAndRedirectToLogin();
      return throwError(() => new Error("No refresh token"));
    }

    return from(this.authenticationService.refreshSession("401-retry")).pipe(
      catchError((err) => {
        this.authenticationService.clearSessionAndRedirectToLogin();
        return throwError(() => err);
      }),
      switchMap((accessToken) => {
        if (!accessToken) {
          this.authenticationService.clearSessionAndRedirectToLogin();
          return throwError(() => new Error("Session refresh failed"));
        }
        const retriedRequest = this.addTokenHeader(
          request.clone({
            context: request.context.set(RETRIED_AFTER_REFRESH, true),
          }),
          accessToken
        );
        return next.handle(retriedRequest);
      })
    );
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}
